// src/pages/PLC.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box
} from "@mui/material";

function PLC() {
  const [data, setData] = useState(null);
  const [latestValues, setLatestValues] = useState({});
  const [plcVars, setPlcVars] = useState({});
  const navigate = useNavigate();

  const fetchPLCs = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch("/api/plc", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
      return;
    }
    const plcData = await res.json();
    setData(plcData);

    // Build flat list of PLC objects
    let plcList = [];
    if (plcData.role === "admin") {
      plcList = Object.values(plcData.plcs).flat();
    } else {
      plcList = plcData.plcs;
    }

    const latestMap = {};
    const varsMap = {};
    await Promise.all(
      plcList.map(async (plc) => {
        try {
          const plcRes = await fetch(`/api/plc/${plc.id}/latest`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (plcRes.ok) {
            const plcValues = await plcRes.json();
            latestMap[plc.id] = plcValues;
          } else {
            latestMap[plc.id] = {};
          }
        } catch (err) {
          console.error("Error fetching latest for", plc.id, err);
          latestMap[plc.id] = {};
        }

        try {
          const vRes = await fetch(`/api/plc/${plc.id}/important_vars`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (vRes.ok) {
            const json = await vRes.json();
            varsMap[plc.id] = json.important_vars || [];
          } else {
            varsMap[plc.id] = [];
          }
        } catch (err) {
          console.error("Error fetching important vars for", plc.id, err);
          varsMap[plc.id] = [];
        }
      })
    );

    setLatestValues(latestMap);
    setPlcVars(varsMap);
  };

  useEffect(() => {
    fetchPLCs();
    const interval = setInterval(fetchPLCs, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!data) return <p>Loading...</p>;

  // 🧩 Render one table per PLC, without the "PLC Name" column
  const renderTable = (plcs) => (
    <div>
      {plcs.map((plc) => {
        const varList = (plcVars[plc.id] && plcVars[plc.id].length > 0)
          ? plcVars[plc.id]
          : Object.keys(latestValues[plc.id] || {});

        return (
          <TableContainer key={plc.id} component={Paper} sx={{ maxWidth: 1200, mb: 4 }}>
            {/* Title already shows the PLC name */}
            <Typography sx={{ p: 2 }} variant="h6">{plc.name}</Typography>

            <Table>
              <TableHead>
                <TableRow>
                  {/* ❌ Removed PLC Name column */}
                  {varList.map((v) => (
                    <TableCell key={v} sx={{ textAlign: "center" }}>
                      <b>{v}</b>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/plc/${plc.id}`)}
                >
                  {/* ❌ Removed PLC Name cell */}
                  {varList.map((v) => (
                    <TableCell key={v} sx={{ textAlign: "center" }}>
                      {latestValues[plc.id]?.[v]?.value ?? "-"}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        );
      })}
    </div>
  );

  return (
    <div>
      <Navbar />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "70vh",
          pt: "100px",
          px: 2,
        }}
      >
        <Typography variant="h4" gutterBottom>
          Welcome, {data.username}
        </Typography>

        {data.role === "admin" ? (
          <div>
            {Object.entries(data.plcs)
              .filter(([client]) => client !== "admin")
              .map(([client, plcs]) => (
                <Box key={client} sx={{ mb: 6 }}>
                  <Typography variant="h6" gutterBottom>
                    {client}
                  </Typography>
                  {plcs.length > 0 ? (
                    renderTable(plcs)
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No PLCs assigned to {client}.
                    </Typography>
                  )}
                </Box>
              ))}
          </div>
        ) : (
          <div>
            {data.plcs.length > 0 ? (
              renderTable(data.plcs)
            ) : (
              <Typography variant="body1" color="text.secondary">
                You don’t have any PLCs assigned yet. Please contact the administrator.
              </Typography>
            )}
          </div>
        )}
      </Box>
      <Footer />
    </div>
  );
}

export default PLC;
